import {
  AcceptTermsCheckbox,
  AsyncActionButton,
  ESessionStorageKeys,
  ExceptionErrorCode,
  getFirstOriginationFromStorage, isEquivalentToSavedOrigination,
  reportErrorToConsole,
  reportErrorToServer,
  ScrollableAlert
} from '@applyonline/shared'
import { EApplicantType } from '@if/core/types'
import { formatCurrency, getEnvRuntimeVariable } from '@if/core/utils'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  SubmitDraftCreditApplicationDto,
  usePrequalify,
  useSubmitDraftCreditApplication,
} from '../../../api/credit-api'
import { useAppStore } from '../../../data/AppContext'
import { Prequalification } from '../../../types/Schemas/PrequalificationSchema'
import ReviewApplicantInfo from './ReviewApplicantInfo'

interface Props {
  gotoStep: (step: string) => void
  prequalification: Prequalification
}

const ReviewPrequalification = ({ prequalification, gotoStep }: Props) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [submitDraftCreditApplication, isSubmitting, isSuccess] = useSubmitDraftCreditApplication()
  const [prequalify] = usePrequalify()
  const [hasDuplicateLoanRequest, setHasDuplicateLoanRequest] = useState(false)
  const [isHandlerRunning, setIsHandlerRunning] = useState(false)

  const appContext = useAppStore()

  const applicant = prequalification.applicant

  const buildDtoAndSubmitPrequal = async (evt: { preventDefault: () => void }) => {
    evt.preventDefault()
    if (isHandlerRunning) return
    setIsHandlerRunning(true)
    try {
      if (!executeRecaptcha) {
        reportErrorToConsole('ReCAPTCHA non disponible.')
        return
      }

      let firstOrigination = getFirstOriginationFromStorage();
      let originationFromUrl = appContext.originationFromUrl;

      if (originationFromUrl == null || isEquivalentToSavedOrigination(originationFromUrl))
      {
        originationFromUrl = firstOrigination;
        firstOrigination = null;
      }

      const recaptchaToken = await executeRecaptcha('submit_postDraftCreditApplication')
      const dto: SubmitDraftCreditApplicationDto = {
        recaptchaToken,
        prequalification: {
          ...prequalification,
          lastTouchOrigination: originationFromUrl,
          firstTouchOrigination: firstOrigination,
        },
      }
      const creditApp = await submitDraftCreditApplication(dto)
      sessionStorage.removeItem(ESessionStorageKeys.ORIGINATION)

      if (getEnvRuntimeVariable<boolean>(import.meta.env.VITE_USE_VOUCHED)) {
        navigate(`/creditApplication/${creditApp.id}/vouched`)
      } else {
        await prequalify({ creditApplicationId: creditApp.id, vouchedJobToken: 'dev', applicantType: EApplicantType.Applicant })
        sessionStorage.removeItem(ESessionStorageKeys.PREQUAL_INFO)
        navigate(`/creditApplication/${creditApp.id}`)
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ errorCode: string }>
      const errorCode = axiosError.response?.data?.errorCode
      if (axiosError.response?.status === 422 && errorCode === ExceptionErrorCode.RecaptchaException) {
        navigate('/recaptcha-validation-failed')
      } else if (errorCode === ExceptionErrorCode.DuplicateException) {
        setHasDuplicateLoanRequest(true)
      } else {
        reportErrorToServer(error as Error)
      }
    } finally {
      setIsHandlerRunning(false)
    }
  }
  return (
    <section className="step-content">
      <h3 className="form-question">{t('review.title')}</h3>

      <div className="paragraph">
        <p>{t('review.description')}</p>
      </div>

      <form onSubmit={buildDtoAndSubmitPrequal}>
        <ScrollableAlert
          showAlert={hasDuplicateLoanRequest}
          type="warn"
          message={t('contact.hasActiveLoanRequest')}
          link={t('contact.icebergPhone')}
          linkText={t('contact.icebergPhone')}
          linkType="tel"
        />
        <div className="review-wrap">
          <div className="review-item">
            <h1 className="h3">{t('review.desiredLoanAmount')}</h1>
            <div className="box">
              <div className="box-content">
                <p>
                  <strong>{t('loanAmount.amount')} : </strong>
                  <span>{formatCurrency(prequalification.requestedLoanAmount)}</span>
                </p>
              </div>
              <button type="button" className="btn-action btn-simple" onClick={() => gotoStep('loan-amount')}>
                {t('review.modify')}
              </button>
            </div>
          </div>
          <ReviewApplicantInfo applicant={applicant} gotoStep={gotoStep} />
        </div>
        <footer className="review-footer">
          <h3>
            <Trans t={t} i18nKey="confirmation.consentTitle" />
          </h3>

          <div className="paragraph">
            <AcceptTermsCheckbox
              msgOnInvalid={t('common.acceptConditions')}
              id="acceptConditions"
              termId={t('confirmation.consent')}
            />

            <AcceptTermsCheckbox msgOnInvalid={t('common.acceptConditions')} id="acceptAuthorise">
              {t('confirmation.authorization')}
              <a href={t('common.policyLink')} target="_blank" rel="noreferrer" className="blue-link">
                {' '}
                {t('contact.linkText')} <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
              {t('confirmation.authorizationEnd')}
            </AcceptTermsCheckbox>
            <AcceptTermsCheckbox msgOnInvalid={t('common.acceptConditions')} id="acceptVouched">
              {t('confirmation.vouchedConsent')}
              <a href="/digital-verification-policy" target="_blank" rel="noreferrer" className="blue-link">
                {' '}
                {t('confirmation.vouchedPolicy')} <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
              {', '}
              {t('confirmation.confirm')}
            </AcceptTermsCheckbox>
          </div>

          <AsyncActionButton
            type="submit"
            className="btn btn-blue"
            disabled={isHandlerRunning || isSubmitting || isSuccess}
            isPending={isHandlerRunning || isSubmitting}
            primary
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {t('review.getQuote')}
          </AsyncActionButton>
          <div className="box">
            <div className="box-content">
              <div className="text-center strong" style={{ marginTop: '2.5rem' }}>
                {t('eligibility.preApproved')}
              </div>
            </div>
          </div>
        </footer>
      </form>
    </section>
  )
}

export default ReviewPrequalification
